import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef } from "react";
import { ErrorBoundary } from "./app/common/error_boundary";

//* interface
// SceneInterpreter(콕핏: deck.gl/mapbox/HUD 등 무거운 의존성)는 차량 선택(연결) 후에만
// 마운트되도록 lazy + 조건부 렌더. → ① 초기/로그인 번들 경량화(코드 스플리팅)
//                                  ② 디버그 툴은 선택 후에만 존재(이전엔 항상 마운트)
const SceneInterpreter = lazy(() =>
  import("./interpreter/scene_interpreter").then((m) => ({
    default: m.SceneInterpreter,
  })),
);
import { LoginScreen } from "./interpreter/login";
import SocketInterface from "./interface/socket_interface";
import SocketMqttBridge from "./interface/socket_mqtt_bridge";

import config from "./interface/web_interface";
import { createCameraMediaSinkRegistry } from "./app/display/media_sink_registry";
import { useExternalDisplayManager } from "./app/display/use_external_display_manager";
import { acquireCockpitDevicePermissions } from "./app/display/acquire_devices";
import { useCameraReceiverFps } from "./hooks/useCameraReceiverFps";
import useRemoteClientState from "./store/hook/useRemoteClientState";
import useRemoteMediaTracks from "./hooks/remoteClient/useRemoteMediaTracks";
import useRemoteSystemStatusPublisher from "./hooks/remoteClient/useRemoteSystemStatusPublisher";
import useOperatorMicTrack from "./hooks/remoteClient/useOperatorMicTrack";
import {
  applyCameraFpsCaps,
  useCameraFpsCaps,
} from "./hooks/remoteClient/camera_fps_cap_store";
import { setRearCameraAvailable } from "./hooks/directControl/rear_camera_store";
import { installCockpitAudioUnlock } from "./app/sound/cockpit_audio_context";
import { installCameraFaultKeyHandler } from "./hooks/remoteClient/camera_fault_store";
import useVehicleSessionGate from "./hooks/remoteClient/useVehicleSessionGate";
import { createDataChannelMessageHandler } from "./utils/remoteClient/dataChannelMessages";
import { OfflineBanner } from "./app/common/offline_banner";
import { WheelDisconnectBanner } from "./app/common/wheel_disconnect_banner";

/**
 * @brief: ad_remote_client application
 */

interface AdRemoteClientProps {
  baseUrl?: string;
}

export function AdRemoteClient({ baseUrl = "/" }: AdRemoteClientProps) {
  const socketRef = useRef<SocketInterface | null>(null);
  if (!socketRef.current) {
    socketRef.current = new SocketInterface(config);
  }

  const bridgeRef = useRef<SocketMqttBridge | null>(null);
  if (!bridgeRef.current && socketRef.current) {
    bridgeRef.current = new SocketMqttBridge(socketRef.current, config);
  }

  const mediaSinkRegistryRef = useRef<ReturnType<
    typeof createCameraMediaSinkRegistry
  > | null>(null);
  if (!mediaSinkRegistryRef.current) {
    mediaSinkRegistryRef.current = createCameraMediaSinkRegistry();
  }
  const {
    cameraReceiverFps,
    startReceiverFpsMonitor,
    stopReceiverFpsMonitor,
  } = useCameraReceiverFps();
  const externalDisplayManager = useExternalDisplayManager(
    baseUrl,
    mediaSinkRegistryRef.current,
    cameraReceiverFps,
  );
  // 장치 준비 단계용 — 보조 모니터 role 별 세부 상태(열림 + 몰입)를 노출한다.
  //  ① 다중 모니터가 아니면(단일/미지원) 펼칠 게 없으므로 준비됨(N/A=ready)
  //  ② 다중 모니터면 열려야 할 모든 role 의 윈도우가 "열림(open)" + "몰입(fullscreen)"이어야 준비됨.
  //     여기서 fullscreen 은 팝업이 보고한 immersive 상태 = 진짜 전체화면 OR "주소창만 있는
  //     전체화면(모니터를 가득 채운 maximized)". 페달로 창을 열면 화면 좌표로 위치/크기 보정되어
  //     maximized=immersive 가 되므로, 이 상태까지 도달해야 다음(차량 선택)으로 넘어간다.
  const deviceSetupDisplayStatuses = externalDisplayManager.openableRoles.map(
    (role) => {
      const state = externalDisplayManager.layoutState[role];
      const win = state?.windowRef ?? null;
      const open = Boolean(win) && !win!.closed;
      return {
        role,
        open,
        fullscreen: open && Boolean(state?.fullscreen),
        windowRef: open ? win : null,
      };
    },
  );
  const deviceSetupDisplaysReady =
    !externalDisplayManager.hasExtendedDisplays ||
    deviceSetupDisplayStatuses.every((s) => s.open && s.fullscreen);

  const { data, setData } = useRemoteClientState();
  const {
    isDisconnectingVehicleSession,
    showLoginScreen,
    disconnectVehicleSession,
  } = useVehicleSessionGate(bridgeRef.current);
  // 개발자 도구(FPS 상한 모달)로 카메라별 상한을 걸면, MQTT 로 내보내는 값만
  // min(실측, 상한) 으로 깎는다. 화면 표시용 cameraReceiverFps 는 손대지 않는다.
  const cameraFpsCaps = useCameraFpsCaps();
  const cameraReceiverFpsForPublish = useMemo(
    () => applyCameraFpsCaps(cameraReceiverFps, cameraFpsCaps),
    [cameraReceiverFps, cameraFpsCaps],
  );
  useRemoteSystemStatusPublisher(bridgeRef.current, cameraReceiverFpsForPublish);

  // 오디오 자동재생 해제 안전망 — 게이밍 휠 로그인은 WebHID 입력이라 브라우저가
  // 사용자 제스처로 인정하지 않아, 로그인 핸들러의 unlock 이 조용히 실패한다.
  // 실제 포인터/키 입력이 한 번이라도 있으면 그때 풀린다.
  useEffect(() => installCockpitAudioUnlock(), []);

  // 영상 장애 모사 단축키(Alt+1 끊김 / Alt+2 순간 블랙) — 메인 창에만 설치한다.
  // 보조 팝업은 레지스트리(이 창)가 만든 스트림을 받으므로 따로 설치할 필요가 없다.
  useEffect(() => installCameraFaultKeyHandler(window), []);

  // 후방 카메라 수신 여부를 스토어로 발행한다. 영상이 없으면 직접제어에서 R 명령을
  // 아예 내보내지 않는다(뒤를 못 보는 채로 후진하지 않도록). 여기서 발행하는 이유는
  // 이 컴포넌트가 항상 마운트되어 있어 HUD 마운트 여부와 무관하게 값이 유지되기 때문이다.
  useEffect(() => {
    setRearCameraAvailable(Number(cameraReceiverFps?.["rear-cam"] ?? 0) > 0);
  }, [cameraReceiverFps]);

  // 콕핏 마이크(→차량 스피커) 아웃바운드 트랙 — 반드시 여기(항상 마운트되는 최상위)에서
  // 등록한다. offer 는 시그널링 이벤트로 시작되는데 콕핏 화면(SceneInterpreter/Communicator)
  // 은 "연결 완료 후"에야 lazy 마운트되므로, 거기서 등록하면 최초 연결 offer 생성 시점에
  // 핸들러가 없어 마이크 트랙이 빠진 채 협상된다(차량 스피커 무음).
  const { prewarmMic } = useOperatorMicTrack(socketRef.current);

  // 차량 세션 연결 완료 콜백 — 콕핏 진입 전환 자체는 useVehicleSessionGate 가 담당하므로
  // 여기서는 추가 동작이 없다. LoginScreen 의 선택적 콜백을 명시적으로 연결해 미사용 prop 을 해소.
  const handleVehicleSessionConnected = useCallback(() => {}, []);

  // 로그인 버튼(사용자 제스처) 시점에 호출 — 다중 모니터를 즉시 점유하고
  // 마이크/휠 등 입출력 장치 권한을 일괄 사전 획득한다(연결 전에 미리 셋업).
  //
  // 액션 순서가 중요하다(하나의 transient activation 을 여러 API 가 경쟁한다):
  //  1) 보조 모니터 팝업(window.open) — 가장 활성화에 민감하므로 "어떤 await 보다도 먼저",
  //     그리고 기기 권한 요청(getUserMedia/hid.requestDevice)보다도 먼저 호출한다.
  //  2) 기기 권한(마이크/휠)은 비동기로 뒤이어 요청(팝업 확보 후).
  //  (메인 창 전체화면 requestFullscreen 은 login.tsx onClickLoginButton 에서 이 콜백 직전에
  //   호출된다 — 전체화면은 팝업 허용량을 소비하지 않으므로 먼저 와도 안전하다.)
  const handleLoginGesture = useCallback(() => {
    // 1) 제스처 즉시(동기) 보조 모니터 팝업을 연다 — await 전에 window.open 해야 팝업 차단 회피.
    externalDisplayManager.openAvailableDisplaysSync();
    // 2) 비동기 보강(첫 권한 부여/누락 role 보완) — 주 경로 아님(gap-filler).
    void externalDisplayManager.autoOpenAvailableDisplays().catch(() => {});
    // 3) 기기 권한은 팝업을 먼저 확보한 뒤 요청.
    void acquireCockpitDevicePermissions();
    // 4) 마이크 사전 워밍 — 권한이 방금 부여되므로, 연결 직후 offer 가 즉시 트랙을 붙일 수 있다.
    prewarmMic();
  }, [externalDisplayManager, prewarmMic]);

  // 로그인 화면이 보일 때 window-management 권한/화면 개수를 미리 확보 — 클릭 시점에
  // screenCount 가 정확해 getAutoOpenRoles 가 올바른 role(2모니터→right, 3+→left+right)을 연다.
  // (첫 로그인에 권한 미부여로 screenCount=0 이던 문제 완화.) 미지원/거부는 안전하게 no-op.
  useEffect(() => {
    if (!showLoginScreen) {
      return;
    }
    void externalDisplayManager.ensureScreenPermission().catch(() => {});
  }, [showLoginScreen, externalDisplayManager]);

  // 콕핏 진입(로그인 화면 → 콕핏 전환) 1회당 한 번만 외부 디스플레이를 자동으로 채운다.
  // showLoginScreen 이 다시 true(연결 해제)가 되면 가드를 재무장해 다음 진입 때 다시 동작한다.
  const autoOpenArmedRef = useRef(false);
  useEffect(() => {
    if (showLoginScreen) {
      // 로그인/플릿 화면으로 돌아오면 다음 콕핏 진입을 위해 가드를 초기화.
      autoOpenArmedRef.current = false;
      return;
    }

    if (autoOpenArmedRef.current) {
      return;
    }
    autoOpenArmedRef.current = true;

    // 외부 디스플레이 자동 열기만 수행한다. 메인 윈도우 전체화면은 사용자 제스처가
    // 필요하므로(브라우저가 transient activation 없는 requestFullscreen 을 거부) 여기서
    // 호출하지 않는다 — 로그인 버튼 클릭(login.tsx onClickLoginButton → requestAppFullscreen)
    // 의 제스처 컨텍스트에서 처리한다.
    void (async () => {
      try {
        await externalDisplayManager.autoOpenAvailableDisplays();
      } catch (_err) {
        // 단일 모니터/미지원 브라우저/권한 거부 등은 조용히 무시(메인 윈도우만 사용).
      }
    })();
  }, [showLoginScreen, externalDisplayManager]);

  useRemoteMediaTracks({
    socket: socketRef.current,
    config,
    mediaSinkRegistry: mediaSinkRegistryRef.current,
    startReceiverFpsMonitor,
    stopReceiverFpsMonitor,
  });

  useEffect(() => {
    const mediaSinkRegistry = mediaSinkRegistryRef.current;

    return () => {
      mediaSinkRegistry.clear();
    };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    socket.registerEvent(
      "message",
      createDataChannelMessageHandler(setData),
      null,
    );
  }, [setData]);

  return (
    <>
      <OfflineBanner />
      <WheelDisconnectBanner />
      {/* 콕핏은 차량 선택(연결) 후에만 마운트 — 로그인 화면에서는 무거운 콕핏 청크를
          내려받거나 디버그 툴을 마운트하지 않는다. */}
      {!showLoginScreen ? (
        <ErrorBoundary>
        <Suspense fallback={<CockpitLoadingFallback />}>
          <SceneInterpreter
            socket={socketRef.current}
            bridge={bridgeRef.current}
            data={data}
            showStatusHeader={true}
            mediaSinkRegistry={mediaSinkRegistryRef.current}
            externalDisplayLayoutState={externalDisplayManager.layoutState}
            externalDisplayControlsEnabled={
              externalDisplayManager.hasExtendedDisplays
            }
            openExternalDisplayRoles={externalDisplayManager.openableRoles}
            openExternalDisplay={externalDisplayManager.openDisplay}
            onDirectControlActiveChange={
              externalDisplayManager.setDirectControlActive
            }
            onRttWarningChange={externalDisplayManager.setWarning}
            cameraReceiverFps={cameraReceiverFps}
            onDisconnectVehicleSession={disconnectVehicleSession}
            isDisconnectingVehicleSession={isDisconnectingVehicleSession}
          ></SceneInterpreter>
        </Suspense>
        </ErrorBoundary>
      ) : (
        <LoginScreen
          bridge={bridgeRef.current}
          onVehicleSessionConnected={handleVehicleSessionConnected}
          onLoginGesture={handleLoginGesture}
          onReopenDisplays={externalDisplayManager.openAvailableDisplaysSync}
          showReopenDisplays={
            externalDisplayManager.hasExtendedDisplays &&
            externalDisplayManager.hasPendingDisplays
          }
          displaysSupported={externalDisplayManager.hasExtendedDisplays}
          displaysReady={deviceSetupDisplaysReady}
          displayStatuses={deviceSetupDisplayStatuses}
          onOpenDisplays={externalDisplayManager.openAvailableDisplaysSync}
        ></LoginScreen>
      )}
      <div className="footage">
        <p>(c) 2022-2026 ACEWORKS Korea Inc. Copyrights. all rights reserved</p>
      </div>
    </>
  );
}

function CockpitLoadingFallback() {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        gap: 12,
        minHeight: "100vh",
        font: "600 13px var(--aw-font, system-ui)",
        color: "#9aa1ac",
        background: "#142134",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        <SpinnerIcon />
        <span>불러오는 중…</span>
      </div>
    </div>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      aria-hidden="true"
      style={{
        animation: "aw-spin 0.9s linear infinite",
      }}
    >
      <style>{`
        @keyframes aw-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .aw-spinner { animation: none; } }
      `}</style>
      <circle cx="14" cy="14" r="11" stroke="#2f5d8c" strokeWidth="3" />
      <path
        d="M14 3 A11 11 0 0 1 25 14"
        stroke="#3a8fd0"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
